/**
 * Navigation nach dem Abschnitt SIDEBAR des Entwurfs.
 *
 * Enthalten: Neu-Schaltflaeche mit Aufklappmenue (8.2), Menuefilter, Gruppen
 * mit Klartext-Erklaerung und Adminmarke, Eintraege mit Bereichsfarbe,
 * Untertitel, Zaehler und Sperrmarke, Fusszeile mit Rail-Schalter und
 * Erklaerungsschalter.
 *
 * Zuschnitt wie in der Vorlage: abgeschaltete Module **existieren nicht** —
 * sie werden nicht ausgegraut. Fehlt nur das Recht, bleibt der Eintrag
 * sichtbar und meldet beim Klick, welches Recht fehlt.
 */

import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { stil } from './stil';
import { useDrawer } from './Drawer';
import { useGemerkt, useGemerktJson } from './speicher';
import { symbol } from '../entwurf/katalog';
import { NEU_ARTEN } from '../entwurf/huelle';

/** Tausendertrennung wie ueberall sonst in der Anwendung. */
const zahlF = new Intl.NumberFormat('de-DE');
import { NAVIGATION, routeErlaubt, zielErlaubt, type Eintrag, type Profil } from '../entwurf/navigation';

/** Bereichsfarbe je Gruppe — woertlich aus `NAV_FARBE`. */
const NAV_FARBE: Record<string, string> = {
  'Start': '--brand-primary',
  'Rollen & Berechtigungen': '--art-rolle',
  'IT-Assets': '--art-app',
  'Organisation': '--art-oe',
  'Anträge & Workflows': '--art-mail',
  'Governance': '--krit-hoch',
  'System': '--art-hw',
  'Regelwerk': '--art-hw',
  'Dev': '--art-gruppe',
};

/**
 * Beim ersten Besuch eingeklappte Gruppen — `true` heisst zu.
 *
 * Offen bleiben `Start`, `Rollen & Berechtigungen` und `IT-Assets`.
 */
const VORGABE_ZU: Record<string, boolean> = Object.fromEntries(
  Object.keys(NAV_FARBE)
    .filter((g) => !['Start', 'Rollen & Berechtigungen', 'IT-Assets'].includes(g))
    .map((g) => [g, true]),
);

/**
 * Stand der gemerkten Navigationseinstellungen.
 *
 * Eine Vorgabe gilt nur für den, der noch nichts gespeichert hat. Wer die
 * Anwendung vorher benutzt hat, trägt seinen alten Zustand im Browser — und
 * für ihn ändert sich durch eine neue Vorgabe **gar nichts**. Genau das ist
 * am 14.08.2026 aufgefallen: die Gruppen standen weiterhin alle offen und die
 * Beschreibungen an, obwohl beides längst umgestellt war.
 *
 * Ein Zähler im Schlüsselnamen löst das ohne Rateverfahren: neuer Name, kein
 * gespeicherter Wert, Vorgabe greift. Wer danach etwas umstellt, behält es
 * wieder. **Heraufzählen nur**, wenn eine geänderte Vorgabe auch bei
 * Bestandsnutzern ankommen soll — sonst wirft man ihnen bei jeder
 * Auslieferung ihre Einstellungen weg.
 */
const STAND = 2;

export interface SeitenleisteProps {
  /**
   * Traegt der Angemeldete dieses Recht **wirklich**?
   *
   * Vorher stand hier `darf([persona], recht)` — und `persona` ist ein im
   * Browser gespeicherter Wert mit der Vorgabe `IAM Admin`, also `*`. Die
   * Sperre hing damit an einer Anzeigeoption, nicht an den Rechten aus
   * `/api/ich`. Sie griff nie, und die Meldung „Ihre Anwendungsrolle IAM
   * Admin fuehrt es nicht" nannte eine Rolle, die das Konto gar nicht trug.
   *
   * `App.tsx` reicht jetzt die echte Pruefung herein: `rechte` aus
   * `/api/ich`, und solange nichts geladen ist, lautet die Antwort **nein**.
   */
  darfDas: (recht: string | null) => boolean;
  profil: Profil;
  /** Auf schmalen Fenstern liegt die Navigation als Overlay ueber dem Inhalt. */
  offen: boolean;
  onSchliessen: () => void;
  /** Version fuer den Verweis in der Fusszeile. */
  version: string;
  /**
   * Zahlen je Pfad, aus dem Bestand. Fehlt ein Pfad, steht dort keine Zahl.
   *
   * Bewusst keine Vorgabe und kein `?? 0`: eine Null waere eine Aussage
   * ueber den Bestand, und solange nichts geladen ist, waere sie falsch.
   */
  zaehler: Record<string, number>;
  /**
   * Breite der Leiste — `breit` oder `rail`.
   *
   * Der Zustand lag bis zum 25.08.2026 in dieser Datei. Damit hatte die
   * Huelle keinen Zugriff darauf, und das Hamburgersymbol der Kopfzeile
   * schaltete auf breiten Fenstern **nichts**: es kennt nur die Ueberlagerung
   * fuer schmale Fenster, und die gibt es ab 1024 px nicht. Der Kunde hat es
   * am 24.08.2026 als „Symbol ohne Funktion" gemeldet.
   *
   * Er liegt jetzt in `App.tsx`; Kopfzeile und Einklappknopf schalten
   * denselben Wert, und `useGemerkt` merkt ihn sich wie zuvor.
   */
  sidebar: string;
  setzeSidebar: (wert: string) => void;
}

/**
 * Aktiver Eintrag: es gewinnt genau einer — der laengste passende
 * Routen-Praefix. Sonst waeren bei `/rollen/IT-IP-01/bearbeiten` drei
 * Eintraege gleichzeitig markiert.
 */
function aktiveRoute(pfad: string, alle: Eintrag[]): string | null {
  let treffer: string | null = null;
  let beste = -1;
  for (const it of alle) {
    const passt = it.route === '/' ? pfad === '/' : pfad === it.route || pfad.startsWith(it.route + '/');
    if (passt && it.route.length > beste) { beste = it.route.length; treffer = it.route; }
  }
  // Detailrouten ohne eigenen Menuepunkt dem passenden Eintrag zuordnen
  if (/^\/rollen\/.+\/bearbeiten$/.test(pfad)) return '/rollen/bearbeiten';
  if (/^\/rollen\/[^/]+$/.test(pfad) && pfad !== '/rollen/bearbeiten') return '/rollen';
  if (/^\/assets\/[^/]+\/[^/]+$/.test(pfad)) return pfad.replace(/\/[^/]+$/, '');
  if (/^\/personen\/[^/]+$/.test(pfad)) return '/personen';
  if (/^\/organisation\/[^/]+$/.test(pfad)) return '/organisation';
  if (/^\/antraege\/[^/]+$/.test(pfad) && pfad !== '/antraege/neu' && pfad !== '/antraege/meine') {
    return '/antraege/meine';
  }
  if (/^\/rezertifizierung\/[^/]+$/.test(pfad)) return '/rezertifizierung';
  if (/^\/sod\//.test(pfad)) return '/sod';
  return treffer;
}

export function Seitenleiste({
  profil, darfDas, offen, onSchliessen, version, zaehler, sidebar, setzeSidebar,
}: SeitenleisteProps) {
  const ort = useLocation();
  const navigate = useNavigate();
  const drawer = useDrawer();

  /* Aus als Vorgabe. Die Beschreibungen sind beim Kennenlernen hilfreich und
     danach Ballast: sie machen aus jedem Eintrag zwei Zeilen und drueckten die
     unteren Gruppen aus dem Bild. Wer sie will, schaltet sie im Konto-Menue
     ein; die Wahl bleibt gespeichert. */
  const [beschreibungenAn, setzeBeschreibungen] =
    useGemerkt<boolean>('navBeschreibungen' + STAND, false);
  /* Welche Gruppen beim ersten Besuch offen stehen.
   *
   * Vorher stand alles offen — neun Gruppen, und die Liste reichte weit
   * unter den Bildrand. Offen bleiben die drei, mit denen taeglich gearbeitet
   * wird; der Rest klappt auf, wenn man ihn braucht, und die Wahl bleibt
   * gespeichert.
   *
   * Die Namen stehen so in `NAV_FARBE` daneben — wer eine Gruppe umbenennt,
   * muss beide Stellen anfassen. Eine dritte Liste waere ein dritter Ort zum
   * Vergessen; der Test unten haelt sie zusammen. */
  const [zu, setzeZu] = useGemerktJson<Record<string, boolean>>('gruppenZu' + STAND, VORGABE_ZU);
  const [filter, setzeFilter] = useState('');
  const [neuMenu, setzeNeuMenu] = useState(false);

  const breit = sidebar === 'breit';
  /* Hier stand `const admin = persona === 'IAM Admin'` — und damit haing die
     Sichtbarkeit der Gruppe „System" an der **Persona**, einem Wert aus dem
     Browserspeicher mit der Vorgabe `IAM Admin`. Sie stand also ohnehin
     jedem offen; die Entra-Rolle daneben in `App.tsx` wirkte nur auf die
     Suchpalette. Drei Vorstellungen von „Admin" in einer Oberflaeche, von
     denen keine die echte war.
     Jetzt entscheidet ueberall dasselbe: `darfDas`, der Rechtekatalog des
     angemeldeten Kontos. Was fehlt, traegt ein Vorhaengeschloss. */
  const q = filter.trim().toLowerCase();
  const zeigeUnter = breit && beschreibungenAn && !q;

  const alleEintraege = NAVIGATION.flatMap((g) => g.eintraege);
  const aktiv = aktiveRoute(ort.pathname, alleEintraege);

  const gruppen = NAVIGATION.map((g) => {
    // Mandantenzuschnitt: abgeschaltete Module existieren im Profil nicht
    let eintraege = g.eintraege.filter((it) => routeErlaubt(it.route, profil));
    let label = g.titel;
    if (label === 'Anträge & Workflows' && profil.module.antraege === false) {
      label = 'Änderungsfreigaben';
    }
    if (q) {
      eintraege = eintraege.filter((it) =>
        (it.titel + ' ' + it.beschreibung + ' ' + it.begriffe).toLowerCase().includes(q));
    }
    if (!eintraege.length) return null;
    return { label, erklaerung: g.beschreibung, eintraege };
  }).filter((g): g is NonNullable<typeof g> => g !== null);

  const neuWaehlen = (ziel: string) => {
    setzeNeuMenu(false);
    if (ziel === 'wegweiser') {
      // Der Wegweiser haengt an der Kopfzeile; das Tastenkuerzel oeffnet ihn.
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'n' }));
    } else if (ziel.startsWith('form:')) {
      drawer.oeffnen(ziel.slice(5));
    } else {
      navigate(ziel);
    }
  };

  return (
    <nav
      aria-label="Hauptnavigation"
      className="seitenleiste"
      data-breit={breit ? 'ja' : 'nein'}
      data-offen={offen ? 'ja' : 'nein'}
    >
      {breit && (
        <div style={stil(
          'flex:0 0 auto;padding:12px 12px 10px;border-bottom:1px solid var(--border-subtle);' +
          'display:flex;flex-direction:column;gap:8px',
        )}>
          <div data-menu="neu" style={stil('position:relative;display:flex;gap:2px')}>
            <button
              onClick={() => neuWaehlen('wegweiser')}
              aria-haspopup="dialog"
              title="Neu anlegen — alle Möglichkeiten (Tastenkürzel n)"
              style={stil(
                'display:flex;align-items:center;gap:9px;flex:1;min-width:0;height:38px;padding:0 12px;' +
                'background:var(--brand-strong);color:var(--text-on-brand);border:none;' +
                'border-radius:var(--r-md) 0 0 var(--r-md);cursor:pointer;text-align:left;' +
                'font-size:14px;font-weight:600',
              )}
            >
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={stil('flex:0 0 16px')}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span style={stil('flex:1')}>Neu</span>
            </button>
            <button
              onClick={() => setzeNeuMenu((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={neuMenu}
              aria-label="Objektart direkt wählen"
              title="Objektart direkt wählen"
              style={stil(
                'flex:0 0 34px;height:38px;display:grid;place-items:center;background:var(--brand-strong);' +
                'color:var(--text-on-brand);border:none;border-left:1px solid rgb(255 255 255/.28);' +
                'border-radius:0 var(--r-md) var(--r-md) 0;cursor:pointer',
              )}
            >
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {neuMenu && (
              <div role="menu" style={stil(
                'position:absolute;top:42px;left:0;right:0;z-index:var(--z-menue);padding:5px;' +
                'background:var(--surface-card);border:1px solid var(--border-default);' +
                'border-radius:var(--r-md);box-shadow:0 10px 28px rgb(0 0 0/.14);' +
                'display:flex;flex-direction:column',
              )}>
                {NEU_ARTEN
                  .filter(([, ziel, recht]) => zielErlaubt(ziel, profil) && darfDas(recht))
                  .map(([label, ziel]) => (
                  <button key={label} role="menuitem" onClick={() => neuWaehlen(ziel)}
                          className="menueintrag" style={stil(
                    'padding:7px 10px;background:none;border:none;border-radius:var(--r-sm);' +
                    'text-align:left;cursor:pointer;font-size:13px',
                  )}>{label}</button>
                  ))}
              </div>
            )}
          </div>

          <div style={stil('position:relative')}>
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round"
                 style={stil('position:absolute;left:10px;top:10px')}>
              <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" />
            </svg>
            <input
              value={filter}
              onChange={(e) => setzeFilter(e.target.value)}
              placeholder="Menü durchsuchen…"
              aria-label="Navigation durchsuchen"
              autoComplete="off"
              spellCheck={false}
              style={stil(
                'width:100%;height:34px;padding:0 30px 0 31px;background:var(--surface-sunken);' +
                'border:1px solid var(--border-subtle);border-radius:var(--r-md);outline:none;font-size:13px',
              )}
            />
            {!!filter && (
              <button onClick={() => setzeFilter('')} aria-label="Filter leeren" style={stil(
                'position:absolute;right:5px;top:5px;width:24px;height:24px;display:grid;place-items:center;' +
                'background:none;border:none;border-radius:var(--r-sm);color:var(--text-tertiary);cursor:pointer',
              )}>
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <div style={stil('flex:1;overflow-y:auto;overflow-x:hidden;padding:12px 10px 4px')}>
        {!!q && gruppen.length === 0 && (
          <div style={stil('padding:24px 12px;text-align:center')}>
            <p style={stil('margin:0 0 4px;font-size:13px;font-weight:500')}>
              Kein Menüpunkt passt zu „{filter}“
            </p>
            <p style={stil('margin:0 0 12px;font-size:12px;line-height:18px;color:var(--text-secondary)')}>
              Suchen Sie stattdessen nach einer Aufgabe.
            </p>
            <button onClick={() => neuWaehlen('wegweiser')} style={stil(
              'height:30px;padding:0 12px;background:var(--brand-strong);color:var(--text-on-brand);' +
              'border:none;border-radius:var(--r-md);font-size:12px;font-weight:500;cursor:pointer',
            )}>Wegweiser öffnen</button>
          </div>
        )}

        {gruppen.map((g) => {
          const gruppeOffen = q ? true : zu[g.label] !== true;
          const bereichsFarbe = NAV_FARBE[g.label] ?? '--text-secondary';
          return (
            <div key={g.label} style={stil('margin-bottom:14px')}>
              <button
                onClick={() => setzeZu({ ...zu, [g.label]: gruppeOffen })}
                aria-expanded={gruppeOffen}
                style={stil(breit
                  ? 'display:flex;align-items:center;gap:6px;width:100%;padding:5px 8px;margin-bottom:2px;' +
                    'background:none;border:none;cursor:pointer;font-size:13px;line-height:18px;font-weight:600;' +
                    'letter-spacing:.06em;text-transform:uppercase;color:var(--text-tertiary);text-align:left'
                  : 'display:block;width:100%;height:1px;margin:8px 0;padding:0;' +
                    'background:var(--border-subtle);border:none;cursor:default')}
              >
                {breit && (
                  <>
                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                         style={stil('flex:0 0 11px;opacity:.7')}>
                      <path d={gruppeOffen ? 'M6 9l6 6 6-6' : 'M9 6l6 6-6 6'} />
                    </svg>
                    <span>{g.label}</span>
                  </>
                )}
              </button>

              {breit && zeigeUnter && g.erklaerung && (
                <p style={stil(
                  'margin:0 0 6px;padding:0 8px;font-size:12px;line-height:16px;' +
                  'color:var(--text-tertiary);text-wrap:pretty',
                )}>{g.erklaerung}</p>
              )}

              {gruppeOffen && (
                <div style={stil('display:flex;flex-direction:column;gap:2px')}>
                  {g.eintraege.map((it) => {
                    const istAktiv = it.route === aktiv;
                    const gesperrt = !darfDas(it.recht);
                    /* Gesperrt heisst: **kein Link**.
                     *
                     * Vorher stand hier ein `NavLink`, der auf die eigene
                     * Adresse zeigte, den Klick abfing und eine Meldung
                     * zeigte. Das ist dieselbe Klasse wie die vier
                     * Schaltflaechen ohne Wirkung vom 14.08.2026: etwas sieht
                     * bedienbar aus, ist es aber nicht. Es nahm den
                     * Tastaturfokus, kuendigte sich der Vorlesesoftware als
                     * Verweis an und fuehrte nirgendwohin.
                     *
                     * Jetzt steht dort ein `span` mit `aria-disabled`, dem
                     * Vorhaengeschloss und dem Grund im `title`. Wer kein
                     * Recht hat, sieht **dass** und **warum** — und kann nicht
                     * darauf klicken. */
                    if (gesperrt) {
                      return (
                        <span
                          key={it.route}
                          aria-disabled="true"
                          title={it.titel + ' — keine Berechtigung: ' + it.recht}
                          className="nav-eintrag nav-eintrag--gesperrt"
                          style={stil(
                            'position:relative;display:flex;align-items:'
                            + (zeigeUnter ? 'flex-start' : 'center') + ';gap:11px;min-height:36px;padding:'
                            + (breit ? (zeigeUnter ? '8px 10px 8px 12px' : '7px 10px 7px 12px') : '7px 0') + ';'
                            + (breit ? '' : 'justify-content:center;')
                            + 'border-radius:var(--r-md);font-size:13px;line-height:18px;'
                            + 'color:var(--text-tertiary);cursor:default',
                          )}
                        >
                          <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
                               strokeLinejoin="round"
                               style={stil('flex:0 0 17px;' + (zeigeUnter ? 'margin-top:1px;' : ''))}>
                            <path d={symbol(it.zeichen)} />
                          </svg>
                          {breit && (
                            <span style={stil('flex:1;min-width:0')}>
                              <span style={stil('display:flex;align-items:center;gap:8px')}>
                                <span style={stil(
                                  'flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
                                )}>{it.titel}</span>
                                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                     style={stil('flex:0 0 13px')}>
                                  <path d="M6 11h12v10H6zM9 11V8a3 3 0 0 1 6 0v3" />
                                </svg>
                              </span>
                            </span>
                          )}
                        </span>
                      );
                    }
                    return (
                      <NavLink
                        key={it.route}
                        to={it.route}
                        onClick={() => {
                          onSchliessen();
                          setzeFilter('');
                        }}
                        aria-current={istAktiv ? 'page' : undefined}
                        title={gesperrt
                          ? it.titel + ' — keine Berechtigung: ' + it.recht
                          : it.titel + ' — ' + it.beschreibung}
                        className="nav-eintrag"
                        style={stil(
                          'position:relative;display:flex;align-items:' +
                          (zeigeUnter ? 'flex-start' : 'center') + ';gap:11px;min-height:36px;padding:' +
                          (breit ? (zeigeUnter ? '8px 10px 8px 12px' : '7px 10px 7px 12px') : '7px 0') + ';' +
                          (breit ? '' : 'justify-content:center;') +
                          'border-radius:var(--r-md);font-size:13px;line-height:18px;text-decoration:none;' +
                          (istAktiv
                            ? 'color:var(--text-primary);font-weight:600;'
                            : gesperrt ? 'color:var(--text-tertiary);' : 'color:var(--text-secondary);'),
                        )}
                      >
                        <span style={stil(istAktiv
                          ? 'position:absolute;left:0;top:6px;bottom:6px;width:2px;background:var(--brand-primary)'
                          : 'display:none')} />
                        <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
                             style={stil(
                               'flex:0 0 17px;' + (zeigeUnter ? 'margin-top:1px;' : '') + 'color:' +
                               (istAktiv ? 'var(--brand-primary)'
                                 : gesperrt ? 'var(--text-tertiary)' : 'var(' + bereichsFarbe + ')'),
                             )}>
                          <path d={symbol(it.zeichen)} />
                        </svg>
                        {breit && (
                          <span style={stil('flex:1;min-width:0')}>
                            <span style={stil('display:flex;align-items:center;gap:8px')}>
                              <span style={stil('flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap')}>
                                {it.titel}
                              </span>
                              {/* Die Zahl kommt aus dem Bestand. Steht dort
                                  keine, wird auch keine gezeigt — nicht
                                  ersatzweise die aus dem Entwurf. Eine Zahl,
                                  die niemand nachrechnet, wird geglaubt. */}
                              {zaehler[it.route] !== undefined && (
                                <span className="num" style={stil(
                                  'min-width:20px;padding:1px 6px;border-radius:999px;font-size:12px;' +
                                  'font-weight:600;text-align:center;' +
                                  (istAktiv
                                    ? 'background:var(--brand-primary);color:var(--text-on-brand)'
                                    : 'background:var(--surface-sunken);color:var(--text-secondary)'),
                                )}>{zahlF.format(zaehler[it.route] as number)}</span>
                              )}
                              {gesperrt && (
                                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                     style={stil('color:var(--text-tertiary)')}>
                                  <path d="M6 11h12v10H6zM9 11V8a3 3 0 0 1 6 0v3" />
                                </svg>
                              )}
                            </span>
                            {zeigeUnter && it.beschreibung && (
                              <span style={stil(
                                'display:block;margin-top:1px;font-size:12px;line-height:17px;font-weight:400;' +
                                'color:var(--text-tertiary);text-wrap:pretty',
                              )}>{it.beschreibung}</span>
                            )}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={stil(
        'flex:0 0 auto;border-top:1px solid var(--border-subtle);padding:8px 10px;' +
        'display:flex;align-items:center;gap:6px;flex-wrap:wrap',
      )}>
        <button
          onClick={() => setzeSidebar(breit ? 'rail' : 'breit')}
          title={breit ? 'Navigation einklappen' : 'Navigation ausklappen'}
          aria-label={breit ? 'Navigation einklappen' : 'Navigation ausklappen'}
          className="knopf-still"
          style={stil(
            'display:flex;align-items:center;gap:8px;flex:1;height:32px;padding:0 8px;background:none;' +
            'border:none;border-radius:var(--r-md);color:var(--text-secondary);font-size:13px;' +
            'cursor:pointer;text-align:left',
          )}
        >
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d={breit ? 'M15 6l-6 6 6 6M4 4v16' : 'M9 6l6 6-6 6M20 4v16'} />
          </svg>
          {breit && <span>Einklappen</span>}
        </button>
        {breit && (
          <>
            <button
              onClick={() => setzeBeschreibungen(!beschreibungenAn)}
              role="switch"
              aria-checked={beschreibungenAn}
              title="Erklärungen unter den Menüpunkten ein- oder ausblenden"
              style={stil(
                'display:flex;align-items:center;gap:7px;height:32px;padding:0 9px;border-radius:var(--r-md);' +
                'cursor:pointer;font-size:13px;border:1px solid ' +
                (beschreibungenAn ? 'var(--brand-primary)' : 'var(--border-subtle)') +
                ';background:' + (beschreibungenAn ? 'var(--brand-primary-subtle)' : 'none') +
                ';color:' + (beschreibungenAn ? 'var(--brand-strong)' : 'var(--text-secondary)'),
              )}
            >
              <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 11v5M12 7.6v.1" />
              </svg>
              <span>Erklärungen</span>
            </button>
            {/* Ohne Verweis: der Update-Verlauf ist am 21.08.2026 entfallen.
                Die Fassung bleibt stehen — sie beantwortet „welcher Stand
                laeuft hier", und das ist im Betrieb die haeufigste Frage. */}
            <span translate="no"
                  style={stil('font-family:var(--mono);font-size:12px;color:var(--text-tertiary)')}>
              {version}
            </span>
          </>
        )}
      </div>
    </nav>
  );
}
